// Procedural audio via Web Audio API — no external files. Synthesizes short
// SFX and a simple evolving pad for music so the build stays self-contained.

import { MusicMood, musicMood, nightShift } from './musicMood'
import { ambienceBed } from './ambience'
import { intensityMix } from './intensityfx'
import { musicLayers } from './layermix'
import { dayLight } from '../core/daylight'

type SfxName =
  | 'shoot'
  | 'hit'
  | 'killbig'
  | 'crit'
  | 'hurt'
  | 'pickup'
  | 'levelup'
  | 'select'
  | 'boss'
  | 'death'
  | 'evolve'
  | 'chest'
  | 'nuke'
  | 'magnet'

export class AudioManager {
  private ctx: AudioContext | null = null
  private master!: GainNode
  private sfxGain!: GainNode
  private musicGain!: GainNode
  private musicTimer = 0
  private musicStep = 0
  // continuous per-biome ambient bed (low drone + looping filtered-noise wind)
  private ambient: {
    drone: OscillatorNode
    droneGain: GainNode
    windGain: GainNode
    windFilt: BiquadFilterNode
  } | null = null
  private ambientBed = ambienceBed('')
  private started = false

  masterVol = 0.8
  sfxVol = 0.9
  musicVol = 0.5
  shakeEnabled = true

  /** Silence everything (incl. the ambient bed) while the tab is backgrounded. */
  suspend(): void { try { if (this.ctx && this.ctx.state === 'running') void this.ctx.suspend() } catch { /* ignore */ } }
  resume(): void { try { if (this.ctx && this.ctx.state === 'suspended') void this.ctx.resume() } catch { /* ignore */ } }

  /** Must be called from a user gesture to satisfy autoplay policies. */
  ensure(): void {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume()
      return
    }
    const Ctor = window.AudioContext || (window as any).webkitAudioContext
    this.ctx = new Ctor()
    this.master = this.ctx.createGain()
    this.sfxGain = this.ctx.createGain()
    this.musicGain = this.ctx.createGain()
    // brickwall limiter so the now-dense SFX bed never clips
    const limiter = this.ctx.createDynamicsCompressor()
    limiter.threshold.value = -3
    limiter.knee.value = 0
    limiter.ratio.value = 12
    limiter.attack.value = 0.003
    limiter.release.value = 0.25
    this.sfxGain.connect(this.master)
    this.musicGain.connect(this.master)
    this.master.connect(limiter)
    limiter.connect(this.ctx.destination)
    this.applyVolumes()
    this.started = true
  }

  applyVolumes(): void {
    if (!this.ctx) return
    this.master.gain.value = this.masterVol
    this.sfxGain.gain.value = this.sfxVol
    this.musicGain.gain.value = this.musicVol * 0.5
  }

  private tone(freq: number, dur: number, type: OscillatorType, gain: number, slideTo?: number): void {
    if (!this.ctx) return
    const t = this.ctx.currentTime
    const osc = this.ctx.createOscillator()
    const g = this.ctx.createGain()
    osc.type = type
    osc.frequency.setValueAtTime(freq, t)
    osc.detune.value = (Math.random() - 0.5) * 90 // subtle per-shot variation
    if (slideTo) osc.frequency.exponentialRampToValueAtTime(Math.max(20, slideTo), t + dur)
    g.gain.setValueAtTime(0.0001, t)
    g.gain.exponentialRampToValueAtTime(gain, t + 0.005)
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur)
    osc.connect(g)
    g.connect(this.sfxGain)
    osc.start(t)
    osc.stop(t + dur + 0.02)
  }

  private noise(dur: number, gain: number, hp = 800): void {
    if (!this.ctx) return
    const t = this.ctx.currentTime
    const n = Math.floor(this.ctx.sampleRate * dur)
    const buf = this.ctx.createBuffer(1, n, this.ctx.sampleRate)
    const data = buf.getChannelData(0)
    for (let i = 0; i < n; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / n)
    const src = this.ctx.createBufferSource()
    src.buffer = buf
    const filt = this.ctx.createBiquadFilter()
    filt.type = 'highpass'
    filt.frequency.value = hp
    const g = this.ctx.createGain()
    g.gain.value = gain
    src.connect(filt)
    filt.connect(g)
    g.connect(this.sfxGain)
    src.start(t)
  }

  // throttle high-rate sounds (shooting) to avoid clipping/CPU spikes
  private lastShoot = 0
  play(name: SfxName): void {
    if (!this.ctx) return
    switch (name) {
      case 'shoot': {
        const now = this.ctx.currentTime
        if (now - this.lastShoot < 0.04) return
        this.lastShoot = now
        this.tone(420, 0.08, 'square', 0.12, 180)
        break
      }
      case 'hit':
        this.tone(220, 0.05, 'triangle', 0.1, 120)
        break
      case 'killbig':
        // meaty multi-kill thwack (swarm-clear crunch)
        this.tone(180, 0.1, 'square', 0.16, 90)
        this.noise(0.08, 0.1, 500)
        break
      case 'crit':
        this.tone(660, 0.12, 'sawtooth', 0.18, 220)
        this.noise(0.08, 0.08)
        break
      case 'nuke':
        // screen-clear bomb: deep boom + bright sweep
        this.tone(70, 0.7, 'sawtooth', 0.32, 40)
        this.noise(0.5, 0.22, 150)
        this.tone(900, 0.4, 'sine', 0.12, 200)
        break
      case 'magnet':
        this.tone(300, 0.25, 'sine', 0.14, 1400)
        break
      case 'hurt':
        this.tone(160, 0.2, 'sawtooth', 0.22, 60)
        this.noise(0.12, 0.12, 400)
        break
      case 'pickup':
        this.tone(880, 0.06, 'sine', 0.1, 1320)
        break
      case 'levelup':
        this.tone(523, 0.12, 'sine', 0.16)
        setTimeout(() => this.tone(784, 0.18, 'sine', 0.16), 90)
        break
      case 'select':
        this.tone(660, 0.05, 'square', 0.1)
        break
      case 'boss':
        this.tone(80, 0.6, 'sawtooth', 0.3, 50)
        this.noise(0.4, 0.15, 200)
        break
      case 'death':
        this.tone(300, 0.8, 'sawtooth', 0.25, 40)
        break
      case 'evolve':
        this.tone(523, 0.1, 'sine', 0.18)
        setTimeout(() => this.tone(659, 0.1, 'sine', 0.18), 80)
        setTimeout(() => this.tone(988, 0.3, 'sine', 0.2), 160)
        break
      case 'chest':
        this.tone(700, 0.08, 'sine', 0.12, 1000)
        setTimeout(() => this.tone(1000, 0.1, 'sine', 0.12), 70)
        break
    }
  }

  private lastPickup = 0
  /** Pickup blip whose pitch rises with the current kill-combo (cash-register climb). */
  playPickup(combo: number): void {
    if (!this.ctx) return
    const now = this.ctx.currentTime
    if (now - this.lastPickup < 0.03) return
    this.lastPickup = now
    const base = 740 + Math.min(28, combo) * 28 // climbs, then caps
    this.tone(base, 0.05, 'sine', 0.08, base * 1.4)
  }

  // Retro space-funk groove (procedural, no copyright): walking bass + vibraphone
  // chord stabs + hi-hat + a sparse lead, over an i–VI–III–VII progression.
  private mood: MusicMood = musicMood('')

  /** Switch the groove + ambient bed to a biome (called when a run starts). */
  setBiome(mapId: string): void {
    this.mood = musicMood(mapId)
    this.ambientBed = ambienceBed(mapId)
    this.startAmbience()
  }

  /** Loopable white-noise buffer (seconds long) for the wind layer. */
  private makeLoopNoise(seconds: number): AudioBuffer {
    const n = Math.floor(this.ctx!.sampleRate * seconds)
    const buf = this.ctx!.createBuffer(1, n, this.ctx!.sampleRate)
    const d = buf.getChannelData(0)
    for (let i = 0; i < n; i++) d[i] = Math.random() * 2 - 1
    return buf
  }

  /** Start (or re-tune) the continuous per-biome ambient bed: a low drone + filtered wind. */
  private startAmbience(): void {
    if (!this.ctx) return
    const bed = this.ambientBed
    if (!this.ambient) {
      const drone = this.ctx.createOscillator()
      const droneGain = this.ctx.createGain()
      droneGain.gain.value = 0.0001
      drone.connect(droneGain)
      droneGain.connect(this.musicGain)
      const wind = this.ctx.createBufferSource()
      wind.buffer = this.makeLoopNoise(2)
      wind.loop = true
      const windFilt = this.ctx.createBiquadFilter()
      windFilt.type = 'bandpass'
      const windGain = this.ctx.createGain()
      windGain.gain.value = 0.0001
      wind.connect(windFilt)
      windFilt.connect(windGain)
      windGain.connect(this.musicGain)
      drone.start()
      wind.start()
      this.ambient = { drone, droneGain, windGain, windFilt }
    }
    const t = this.ctx.currentTime
    const a = this.ambient
    a.drone.type = bed.droneType
    a.drone.frequency.setValueAtTime(bed.droneFreq, t)
    a.droneGain.gain.setTargetAtTime(bed.droneGain, t, 0.6) // gentle fade between biomes
    a.windFilt.frequency.setValueAtTime(bed.windCutoff, t)
    a.windFilt.Q.value = bed.windQ
    a.windGain.gain.setTargetAtTime(bed.windGain, t, 0.6)
  }

  /** Fade the ambient bed out (run end / menu) — nodes persist, just silenced. */
  stopAmbience(): void {
    if (!this.ctx || !this.ambient) return
    const t = this.ctx.currentTime
    this.ambient.droneGain.gain.setTargetAtTime(0.0001, t, 0.4)
    this.ambient.windGain.gain.setTargetAtTime(0.0001, t, 0.4)
  }

  updateMusic(dt: number, intensity: number, time = 0, enemyCount = 0, bossActive = false): void {
    if (!this.ctx || !this.started) return
    // CO4: swell + speed + drive the groove with live combat pressure (swarm size + boss presence)
    const mix = intensityMix(enemyCount, bossActive)
    this.musicGain.gain.setTargetAtTime(this.musicVol * 0.5 * mix.musicGainMul, this.ctx.currentTime, 0.5)
    this.musicTimer -= dt
    if (this.musicTimer > 0) return
    // darken the groove as the run reaches dusk (mirrors the day→dusk lighting arc)
    const m = nightShift(this.mood, dayLight(time).cool)
    const step = m.step - Math.min(0.05, intensity * 0.006) - mix.tempoBoost // tempo (speeds with danger)
    this.musicTimer = step
    const s = this.musicStep++
    const chord = m.progression[Math.floor(s / 8) % m.progression.length]
    const beat = s % 8

    // walking bass
    this.musicTone(chord[m.bassPat[beat] % chord.length] / 2, step * 1.7, m.bassType, 0.24)
    // CO4: a driving kick on the beat that fades in as the fight escalates
    if (mix.drive > 0.02 && (beat % 2 === 0 || mix.drive > 0.6)) this.kick(0.34 * mix.drive)
    // hi-hat on offbeats — brighter moods keep more presence
    if (beat % 2 === 1) this.musicNoise(0.04, 0.03 + 0.03 * m.brightness, 7000)
    // chord stabs — bright moods lift an octave, gritty moods detune for shimmer
    if (beat === 0 || beat === 4) {
      const oct = m.brightness > 0.55 ? 2 : 1
      for (const f of chord) this.musicTone(f * oct, step * 2.2, m.stabType, 0.06, m.shimmer)
    }
    // sparse space lead in the back half of the loop
    if (Math.floor(s / 8) % m.progression.length >= 2 && beat % 2 === 0) {
      this.musicTone(m.leadScale[(s * 3) % m.leadScale.length] * 2, step * 1.2, m.leadType, 0.04)
    }
    // PT5: extra layers fade in with combat pressure so a long run's soundtrack keeps evolving
    const layers = musicLayers(mix.level, bossActive)
    if (layers.arp && beat % 2 === 1) {
      // a busy high arpeggio fill once the fight is heated
      this.musicTone(m.leadScale[(s * 5) % m.leadScale.length] * 4, step * 0.5, m.leadType, 0.03)
    }
    if (layers.surge && (beat === 0 || beat === 2 || beat === 4 || beat === 6)) {
      // the groove thickens — an extra octave chord stab driving the peak
      this.musicTone(chord[s % chord.length] * 4, step * 0.9, m.stabType, 0.03, m.shimmer)
    }
    if (layers.boss && beat === 0) {
      // a low, menacing boss leitmotif drone on the downbeat
      this.musicTone(chord[0] / 4, step * 3.4, 'sawtooth', 0.1)
    }
  }

  private musicTone(freq: number, dur: number, type: OscillatorType, gain: number, detune = 0): void {
    if (!this.ctx) return
    const t = this.ctx.currentTime
    const osc = this.ctx.createOscillator()
    const g = this.ctx.createGain()
    osc.type = type
    osc.frequency.value = freq
    if (detune) osc.detune.value = detune
    g.gain.setValueAtTime(0.0001, t)
    g.gain.exponentialRampToValueAtTime(gain, t + 0.02)
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur)
    osc.connect(g)
    g.connect(this.musicGain)
    osc.start(t)
    osc.stop(t + dur + 0.05)
  }

  /** CO4: a punchy kick drum (pitch-drop sine thump) for the intensity-driven percussion layer. */
  private kick(gain: number): void {
    if (!this.ctx) return
    const t = this.ctx.currentTime
    const osc = this.ctx.createOscillator()
    const g = this.ctx.createGain()
    osc.type = 'sine'
    osc.frequency.setValueAtTime(120, t)
    osc.frequency.exponentialRampToValueAtTime(44, t + 0.12)
    g.gain.setValueAtTime(Math.max(0.0001, gain), t)
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.16)
    osc.connect(g)
    g.connect(this.musicGain)
    osc.start(t)
    osc.stop(t + 0.18)
  }

  private musicNoise(dur: number, gain: number, hp: number): void {
    if (!this.ctx) return
    const t = this.ctx.currentTime
    const n = Math.floor(this.ctx.sampleRate * dur)
    const buf = this.ctx.createBuffer(1, n, this.ctx.sampleRate)
    const d = buf.getChannelData(0)
    for (let i = 0; i < n; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / n)
    const src = this.ctx.createBufferSource()
    src.buffer = buf
    const filt = this.ctx.createBiquadFilter()
    filt.type = 'highpass'
    filt.frequency.value = hp
    const g = this.ctx.createGain()
    g.gain.value = gain
    src.connect(filt)
    filt.connect(g)
    g.connect(this.musicGain)
    src.start(t)
  }
}
